import { FC, FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Box } from '@tink/core/src/components/atoms/Box/Box';
import { Button } from '../atoms/Button/Button';
import { Footer } from '../atoms/Footer/Footer';
import { ProviderField } from '../../models/provider';
import { SubmitProviderInputsPayload } from '../screens/SubmitCredentials/molecules/ProviderInputs/ProviderInputs';
import { StyledForm } from '../screens/SubmitCredentials/molecules/ProviderInputs/ProviderInputs.styles';
import {
  Input,
  OnChangeProps,
} from '../screens/SubmitCredentials/molecules/ProviderInputs/Input/Input';
import { PageView, UserInteractionEvent } from '../../models/analytics';
import { ContentBody } from '../atoms/ContentBody/ContentBody';
import { usePageViewEvents } from '../../core/Analytics/usePageViewEvents';
import { H1 } from '../atoms/Heading/Heading';
import { Text } from '../atoms/Text/Text';
import { useCredentialsCopy } from '../screens/SubmitCredentials/hooks/useCredentialsCopy';
import { TextAlignmentContainer } from '../TextAlignmentContainer/TextAlignmentContainer';
import { Accordion } from '../atoms/Accordion/Accordion';
import { messages } from './messages';
import { TemplateElementStyle, TemplateElementType, Templates } from './templates';
import { TemplateInputCode } from './TemplateInputCode';
import { useProviderInput } from './useProviderInput';
import { TemplateOrderedList } from './molecules/TemplateOrderedList/TemplateOrderedList';
import { MaybeChangeMethodButton } from './MaybeChangeMethodButton/MaybeChangeMethodButton';

export type CardReaderCodeTemplateProps = {
  fields: ProviderField[];
  onSubmit: (values: SubmitProviderInputsPayload) => void;
};

export const CardReaderCodeTemplate: FC<CardReaderCodeTemplateProps> = ({
  fields: data,
  onSubmit,
}) => {
  const { sendViewInteractionEvent, sendViewInteractionEventOnce } = usePageViewEvents({
    pageView: PageView.CARD_READER_TEMPLATE_SCREEN,
  });

  const handleSubmitWithAnalytics = (values: SubmitProviderInputsPayload) => {
    sendViewInteractionEvent({ interaction: UserInteractionEvent.SUBMIT_SUPPLEMENTAL_INFORMATION });
    onSubmit(values);
  };

  const handleToggle = (label: string) => {
    sendViewInteractionEventOnce({ interaction: UserInteractionEvent.TOGGLE_ACCORDION, label });
  };

  const { formState, updateField } = useProviderInput(data);

  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(true);
  const ref = useRef<HTMLInputElement>(null);

  const fields = useMemo(() => {
    const body = data.find(
      (el) => el.type === TemplateElementType.TEXT && el.style === TemplateElementStyle.TITLE
    );
    const inputStartCode = data.find(
      (el) => el.type === TemplateElementType.TEXT && el.style === TemplateElementStyle.INSTRUCTION
    );
    const inputTan = data.find((el) => el.type === TemplateElementType.INPUT);
    const orderedList = data.find(
      (el) => el.type === TemplateElementType.TEXT && el.style === TemplateElementStyle.ORDERED_LIST
    );

    return { body, inputStartCode, inputTan, orderedList };
  }, [data]);

  useEffect(() => {
    const isAllInputsValid = Object.values(formState).every((formField) => formField.isValid);
    setIsButtonDisabled(!isAllInputsValid);
  }, [formState]);

  const { heading } = useCredentialsCopy();

  const validateInput = ({ providerInputName, value }: OnChangeProps) => {
    updateField(providerInputName, value);
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const inputValues = Object.keys(formState).reduce<Record<string, string>>(
      (values, fieldName) => {
        values[fieldName] = formState[fieldName].value;

        return values;
      },
      {}
    );

    handleSubmitWithAnalytics({ ...inputValues });
  };

  return (
    <ContentBody data-testid="card-reader-code-template">
      <StyledForm onSubmit={handleSubmit} id={Templates.CARD_READER}>
        <H1>{heading}</H1>

        {fields.body && (
          <TextAlignmentContainer>
            <Text color="secondary" mb="medium">
              {fields.body.value}
            </Text>
          </TextAlignmentContainer>
        )}

        {fields.inputStartCode && (
          <Box mb="normal">
            <TemplateInputCode field={fields.inputStartCode} />
          </Box>
        )}

        {fields.inputTan && (
          <Box mb="medium">
            <Input
              ref={ref}
              index={0}
              onChange={validateInput}
              providerInputState={{
                ...formState[fields.inputTan.name],
                providerField: fields.inputTan,
              }}
            />
          </Box>
        )}

        {fields.orderedList && (
          <Box mt="medium" mb="normal">
            <Accordion
              heading={fields.orderedList.description}
              name="instructions"
              onToggle={handleToggle}
            >
              <TemplateOrderedList field={fields.orderedList} renderDescription={false} />
            </Accordion>
          </Box>
        )}

        <Footer stickToBottom>
          <Button disabled={isButtonDisabled} type="submit" color="primary" variant="contained">
            <FormattedMessage {...messages.idCompletionSubmitButton} />
          </Button>
          <MaybeChangeMethodButton elements={data} onSubmit={onSubmit} />
        </Footer>
      </StyledForm>
    </ContentBody>
  );
};
